import type { StandardTextFieldProps } from '@mui/material/TextField';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { TextField, type CustomTextFieldSlotProps } from 'features/ui/elements';

export interface TextareaProps
  extends Omit<StandardTextFieldProps, 'slotProps'> {
  value?: string;
  slotProps?: CustomTextFieldSlotProps;
}

export const Textarea: FC<TextareaProps> = (props) => {
  const { t } = useTranslation('translation', { keyPrefix: 'textarea' });

  const maxLength: number | undefined = props.slotProps?.htmlInput?.maxLength;

  const isMaxLengthExceeded = useMemo(() => {
    if (!props.value || !maxLength) {
      return;
    }

    return props.value.length > maxLength;
  }, [props.value, maxLength]);

  const helperText = useMemo(() => {
    if (!maxLength) {
      return;
    }

    const value = props.value;

    if (!value) {
      return t('max', { count: maxLength });
    }

    if (isMaxLengthExceeded) {
      return t('max-exceeded');
    }

    return `${value.length}/${maxLength}`;
  }, [t, maxLength, props.value, isMaxLengthExceeded]);

  const slotProps = useMemo(() => {
    if (!maxLength) {
      return props.slotProps;
    }

    return {
      ...props.slotProps,
      htmlInput: {
        ...props.slotProps?.htmlInput,
        maxLength: maxLength + 1,
      },
    };
  }, [props.slotProps, maxLength]);

  return (
    <TextField
      {...props}
      type='text'
      multiline
      rows={props.rows || 5}
      fullWidth
      error={props.error || isMaxLengthExceeded}
      slotProps={slotProps}
      helperText={helperText}
    />
  );
};
